import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Truck, X, RotateCcw, ArrowRight, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from './CartContext';
import { loadLatestOrder, getCurrentStepIndex, STEP_ORDER, type TrackedOrder } from '../utils/orderTracking';
// Session-only dismiss key. We intentionally don't use localStorage —
// we want returning visitors to be greeted on every fresh visit.
const SESSION_DISMISS_KEY = 'welcome_back_dismissed_v1';
const VISITS_KEY = '253gravel_visits';
const LAST_ORDER_KEY = '253gravel_last_order';
const REORDER_CHIP_IMPRESSIONS_KEY = 'reorder_chip_impressions_v1';
const REORDER_CHIP_MAX_IMPRESSIONS = 3;
type SavedOrder = {
  items: {
    id?: string;
    category: string;
    product: string;
    quantity: number;
    pricePerYard: number;
  }[];
  addressInput?: string;
  name?: string;
  phone?: string;
  email?: string;
};
// Estimate the total cost for the saved order using the same small-load /
// bulk discount math the cart uses, so the headline number matches what
// the user will see in the cart after reorder.
function estimateOrderTotal(items: SavedOrder['items']): number {
  return items.reduce((sum, item) => {
    const n = (item.product || '').toLowerCase();
    const isLight = n.includes('bark') || n.includes('compost') || n.includes('topsoil') || n.includes('play chips');
    const smallLoadThreshold = 10;
    const billable = item.quantity;
    const surcharge = (() => {
      if (billable < 3) return 1;
      if (billable >= smallLoadThreshold) return 1;
      return 1 + (smallLoadThreshold - billable) * 0.1;
    })();
    const effective = Math.ceil(item.pricePerYard * surcharge);
    const fullLoad = isLight ? 15 : 12;
    const bulkPct = item.quantity >= fullLoad * 4 ? 0.1 : item.quantity >= (isLight ? fullLoad : fullLoad * 2) ? 0.05 : 0;
    let total = billable * effective;
    if (bulkPct > 0) total = Math.round(total * (1 - bulkPct));
    return sum + total;
  }, 0);
}
// Read last-order data from localStorage once and expose it along with a
// stable reorder action. Used by both the WelcomeBackCard and the inline
// ReorderChip so they stay in sync.
function useLastOrder() {
  const {
    loadSavedCart,
    setDeliveryInfo,
    openCart
  } = useCart();
  const [savedOrder, setSavedOrder] = useState<SavedOrder | null>(null);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LAST_ORDER_KEY);
      if (!raw) return;
      const parsed: SavedOrder = JSON.parse(raw);
      if (!parsed?.items?.length) return;
      setSavedOrder(parsed);
    } catch {}
  }, []);
  const handleReorder = () => {
    if (!savedOrder) return;
    loadSavedCart(savedOrder.items.map(i => ({
      id: `${i.category}-${i.product}-${Date.now()}-${Math.random()}`,
      category: i.category,
      product: i.product,
      quantity: i.quantity,
      pricePerYard: i.pricePerYard
    })));
    if (savedOrder.addressInput) {
      setDeliveryInfo(savedOrder.addressInput, 0, null);
    }
    openCart();
  };
  return {
    savedOrder,
    handleReorder
  };
}
// Slim inline pill version used inside the Hero estimator card. Catches
// returning visitors who dismissed the big welcome card but still want
// one-tap reorder from the place they actually start a new estimate.
// Auto-dismisses after REORDER_CHIP_MAX_IMPRESSIONS page loads without a
// reorder — counter is keyed to the saved order's contents so a new
// order resets the visibility window.
export function ReorderChip() {
  const {
    savedOrder,
    handleReorder
  } = useLastOrder();
  const [shouldShow, setShouldShow] = useState(false);
  useEffect(() => {
    if (!savedOrder) {
      setShouldShow(false);
      return;
    }
    try {
      // Stable signature of the saved order — if items change (new order
      // placed), we start the impression count from scratch.
      const sig = JSON.stringify(savedOrder.items.map(i => ({
        p: i.product,
        q: i.quantity,
        c: i.category
      })));
      const raw = localStorage.getItem(REORDER_CHIP_IMPRESSIONS_KEY);
      let data: {
        sig: string;
        count: number;
      } | null = raw ? JSON.parse(raw) : null;
      if (!data || data.sig !== sig) {
        data = {
          sig,
          count: 1
        };
      } else {
        data.count += 1;
      }
      localStorage.setItem(REORDER_CHIP_IMPRESSIONS_KEY, JSON.stringify(data));
      setShouldShow(data.count <= REORDER_CHIP_MAX_IMPRESSIONS);
    } catch {
      setShouldShow(true);
    }
  }, [savedOrder]);
  if (!savedOrder || !shouldShow) return null;
  const firstItem = savedOrder.items[0];
  const summary = savedOrder.items.length > 1 ? `${savedOrder.items.reduce((s, i) => s + i.quantity, 0)} yds × ${savedOrder.items.length}` : `${firstItem.quantity} yds ${firstItem.product}`;
  return <button type="button" onClick={handleReorder} className="group w-full inline-flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/15 hover:border-[#04A05C]/50 transition-all text-left" data-id="element-2158" data-mp="y6qup9zz6krf">
      <span className="flex items-center gap-2.5 min-w-0" data-id="element-2159" data-mp="lvga7y61f1dx">
        <RotateCcw className="w-3.5 h-3.5 text-[#04A05C] shrink-0" data-id="element-2160" data-mp="gyzlp2uxel0b" />
        <span className="text-xs sm:text-sm text-white/80 truncate" data-id="element-2161" data-mp="cx4ly2sn8fon">
          <span className="text-white/60" data-id="element-2162" data-mp="curw73gn9111">Reorder last:</span>{' '}
          <span className="font-semibold text-white" data-id="element-2163" data-mp="19mbxqj3gnafd">{summary}</span>
        </span>
      </span>
      <span className="inline-flex items-center gap-1 text-xs font-bold text-[#04A05C] shrink-0" data-id="element-2164" data-mp="14p28b3g2myrj">
        Reorder
        <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" data-id="element-2165" data-mp="10kcgbpoja4bw" />
      </span>
    </button>;
}
export function WelcomeBackCard() {
  const {
    items,
    loadSavedCart,
    setDeliveryInfo,
    openCart
  } = useCart();
  const [savedOrder, setSavedOrder] = useState<SavedOrder | null>(null);
  const [visits, setVisits] = useState(1);
  const [dismissed, setDismissed] = useState(false);
  // Active-order detection — if the customer has a TrackedOrder saved from
  // this device that hasn't been delivered yet, we swap the "reorder" CTA
  // for a one-tap "Track delivery" CTA. This is the same data the email
  // confirmation links to, but surfaced contextually in the Hero where the
  // customer naturally lands when they return to the site mid-order.
  const [activeOrder, setActiveOrder] = useState<TrackedOrder | null>(null);
  const [activeStepIdx, setActiveStepIdx] = useState<number>(0);
  useEffect(() => {
    try {
      // If user already dismissed this session, stay hidden.
      if (sessionStorage.getItem(SESSION_DISMISS_KEY)) {
        setDismissed(true);
        return;
      }
      const raw = localStorage.getItem(LAST_ORDER_KEY);
      if (!raw) return;
      const parsed: SavedOrder = JSON.parse(raw);
      if (!parsed?.items?.length) return;
      setSavedOrder(parsed);
      // Increment visit counter (used to tune the greeting tone).
      const v = parseInt(localStorage.getItem(VISITS_KEY) || '1', 10);
      setVisits(v);
      localStorage.setItem(VISITS_KEY, String(v + 1));
      // Check for an in-progress order from THIS device. The order is
      // considered "active" until the delivery window closes (step 4).
      const latest = loadLatestOrder();
      if (latest) {
        const stepIdx = getCurrentStepIndex(latest);
        if (stepIdx < STEP_ORDER.length - 1) {
          setActiveOrder(latest);
          setActiveStepIdx(stepIdx);
        }
      }
    } catch {}
  }, []);
  // Don't show if cart already has items — they're mid-order.
  if (dismissed || !savedOrder || items.length > 0) return null;
  const firstName = (savedOrder.name || '').trim().split(/\s+/)[0] || 'there';
  const totalYards = savedOrder.items.reduce((s, i) => s + i.quantity, 0);
  const itemSummary = savedOrder.items.length > 1 ? `${totalYards} yds across ${savedOrder.items.length} materials` : `${savedOrder.items[0].quantity} yds ${savedOrder.items[0].product}`;
  const shortAddress = (savedOrder.addressInput || '').split(',')[0].trim();
  const estTotal = estimateOrderTotal(savedOrder.items);
  // Greeting copy adapts to whether they're tracking an active order or
  // returning between orders. Active-order takes priority — it's the more
  // urgent, helpful surface in that moment.
  const greeting = activeOrder ? `Hi ${firstName} — your load is on the way` : visits >= 2 ? `Need another load, ${firstName}?` : `Welcome back, ${firstName} 👋`;
  // Human label for the active-order step, mapped to the same progression
  // the /track page uses. Keeps the messaging consistent across surfaces.
  const stepLabel = (() => {
    switch (STEP_ORDER[activeStepIdx]) {
      case 'confirmed':
        return 'Order confirmed';
      case 'scheduled':
        return 'Scheduled for delivery';
      case 'dispatched':
        return 'Dispatched from yard';
      case 'en_route':
        return 'Driver en route';
      default:
        return 'In progress';
    }
  })();
  const handleReorder = () => {
    // Load items first so the cart has something to checkout.
    loadSavedCart(savedOrder.items.map(i => ({
      id: `${i.category}-${i.product}-${Date.now()}-${Math.random()}`,
      category: i.category,
      product: i.product,
      quantity: i.quantity,
      pricePerYard: i.pricePerYard
    })));
    // Push the saved delivery address into context. CartDrawer's
    // geocode-on-change effect will recompute fee + zone automatically.
    if (savedOrder.addressInput) {
      setDeliveryInfo(savedOrder.addressInput, 0, null);
    }
    openCart();
  };
  const handleDismiss = () => {
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
    } catch {}
    setDismissed(true);
  };
  return <AnimatePresence data-id="element-2166" data-mp="1ywcdt98zpbd4">
      <motion.div initial={{
      opacity: 0,
      y: -8,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: -8,
      scale: 0.98
    }} transition={{
      duration: 0.35,
      ease: [0.16, 1, 0.3, 1]
    }} className="relative mb-6 md:mb-8 max-w-3xl mx-auto md:mx-0 rounded-xl bg-black/35 backdrop-blur-md border border-white/15 px-4 py-3 sm:px-5 sm:py-3.5 shadow-[0_8px_30px_rgba(0,0,0,0.35)]" role="region" aria-label="Welcome back" data-id="element-2167" data-mp="1yyyj128eres6">
        <div className="flex items-center gap-3 sm:gap-4" data-id="element-2168" data-mp="1tkxasd4d06g">
          <div className="hidden sm:flex items-center justify-center w-10 h-10 shrink-0 rounded-full bg-[#04A05C]/15 border border-[#04A05C]/30" data-id="element-2169" data-mp="1u324ddukmpz">
            <Truck className="w-5 h-5 text-[#04A05C]" data-id="element-2170" data-mp="brh6vfqern2g" />
          </div>

          <div className="flex-1 min-w-0" data-id="element-2171" data-mp="bpnx3rki0mq8">
            <p className="text-sm sm:text-base font-bold text-white leading-tight" data-id="element-2172" data-mp="6s4i2pfz85x8">
              {greeting}
            </p>
            {activeOrder ? <p className="text-xs sm:text-sm text-white/70 mt-0.5 truncate" data-id="element-2173" data-mp="1n5cocv4sc7wu">
                <span className="inline-flex items-center gap-1 text-[#22c95c] font-semibold" data-id="element-2174" data-mp="1i71bscn3vjax">
                  <span className="relative flex h-2 w-2" data-id="element-2175" data-mp="1pqajm1fyr9xv">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#04A05C] opacity-75" data-id="element-2176" data-mp="1uqf0vtop48p5" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#04A05C]" data-id="element-2177" data-mp="1grfgwzc4ohzb" />
                  </span>
                  {stepLabel}
                </span>
                <span className="text-white/40 mx-1.5" data-id="element-2178" data-mp="28htl23ucafxs">·</span>
                <span className="text-white/90 font-medium" data-id="element-2179" data-mp="28ilk5on5avst">{itemSummary}</span>
                {shortAddress && <>
                    {' '}
                    → <span className="text-white/90" data-id="element-2180" data-mp="1zprserqii3y2">{shortAddress}</span>
                  </>}
              </p> : <p className="text-xs sm:text-sm text-white/70 mt-0.5 truncate" data-id="element-2181" data-mp="23qv6b8p01pwn">
                Last load:{' '}
                <span className="text-white/90 font-medium" data-id="element-2182" data-mp="2rgfp9rl5kn5x">{itemSummary}</span>
                {shortAddress && <>
                    {' '}
                    → <span className="text-white/90" data-id="element-2183" data-mp="2w8pef5eg1j75">{shortAddress}</span>
                  </>}
              </p>}
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0" data-id="element-2184" data-mp="321t4m3fav7bi">
            {activeOrder ?
          // One-tap track CTA. Uses the same /track/{orderId} route the
          // confirmation email links to — no order number or email
          // lookup required because the customer is on the device that
          // placed the order. This replaces the "Track your delivery"
          // override that used to live in the announcement bar.
          <Link to={`/track/${encodeURIComponent(activeOrder.orderId)}`} className="group inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg bg-[#04A05C] hover:bg-[#048F3A] text-white font-bold text-xs sm:text-sm transition-colors shadow-[0_0_14px_rgba(4,160,92,0.3)]" aria-label="Track your delivery" data-id="element-2185" data-mp="2id0kdjue6wk9">
                <MapPin className="w-3.5 h-3.5" data-id="element-2186" data-mp="24d99vt333yb0" />
                <span className="hidden sm:inline" data-id="element-2187" data-mp="1zefws1rpm9bd">Track delivery</span>
                <span className="sm:hidden" data-id="element-2188" data-mp="1zhaidwmgm06d">Track</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" data-id="element-2189" data-mp="1pa7tez6fxvi8" />
              </Link> : <button type="button" onClick={handleReorder} className="group inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg bg-[#04A05C] hover:bg-[#048F3A] text-white font-bold text-xs sm:text-sm transition-colors shadow-[0_0_14px_rgba(4,160,92,0.3)]" data-id="element-2190" data-mp="1u8j5lgk7ki3m">
                <RotateCcw className="w-3.5 h-3.5" data-id="element-2191" data-mp="16lb0ozqozb24" />
                <span className="hidden sm:inline" data-id="element-2192" data-mp="16if4pgyemwgb">Reorder · ${estTotal}</span>
                <span className="sm:hidden" data-id="element-2193" data-mp="1bk4hb119zi6i">${estTotal}</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" data-id="element-2194" data-mp="1gifnn1sd3ajz" />
              </button>}
            <button type="button" onClick={handleDismiss} aria-label="Dismiss" className="p-1.5 rounded-md text-white/50 hover:text-white hover:bg-white/10 transition-colors" data-id="element-2195" data-mp="1la7gxk2wjudt">
              <X className="w-4 h-4" data-id="element-2196" data-mp="ssbk0uf6basz" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>;
}